import { Component, OnInit, ViewChild, ChangeDetectorRef, SecurityContext, inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormControl, FormGroup } from '@angular/forms';
import { BehaviorSubject, debounceTime, distinctUntilChanged, startWith, tap } from 'rxjs';
import { DomSanitizer } from '@angular/platform-browser';
import OrganizationService from '../../services/organization.service';
import { Organization, OrganizationSearchEvent } from 'src/app/features/organization/organization-models';
import { PageEvent } from '@angular/material/paginator';
import { LoadingSpinnerComponent } from '../../../core/loading-spinner/loading-spinner.component';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatBadgeModule } from '@angular/material/badge';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatListModule } from '@angular/material/list';

@Component({
  selector: 'adm-organization-selector',
  templateUrl: './organization-selector.component.html',
  styleUrls: ['./organization-selector.component.scss'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatCheckboxModule,
    MatButtonModule,
    MatIconModule,
    MatPaginatorModule,
    MatBadgeModule,
    MatSlideToggleModule,
    MatListModule,
    LoadingSpinnerComponent,
  ],
})
export class OrganizationSelectorComponent implements OnInit {
  protected changeDetectorRef = inject(ChangeDetectorRef);
  dialogRef = inject<MatDialogRef<OrganizationSelectorComponent>>(MatDialogRef);
  data = inject<{
    selectedOrganizations: Organization[];
    maxSelectCount?: number;
  }>(MAT_DIALOG_DATA);
  private organizationService = inject(OrganizationService);
  private sanitizer = inject(DomSanitizer);

  public organizationItems: Organization[] = [];
  public selectedOrganizations: Organization[] = [];
  public maxSelectCount: number | null;
  public disableCheckboxes = false;
  public selectedOrganizationCount = 0;
  public loadedSelectedOrganizations = false;
  @ViewChild('organizations') organizations: Organization[] = [];
  organizationCheckboxes = new FormControl();
  public filterModel: FormGroup = this.initForm();
  public selectAll = false;
  public allAreSelected = false;
  public loading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public organizationSearchEvent: OrganizationSearchEvent = {} as OrganizationSearchEvent;

  get query() {
    return this.filterModel.controls['query'];
  }

  ngOnInit(): void {
    this.loading.next(true);
    this.selectedOrganizations = this.data.selectedOrganizations;

    this.maxSelectCount = this.data.maxSelectCount ?? null;

    const pageEvent = new PageEvent();
    pageEvent.pageSize = 10000;
    this.organizationSearchEvent.paging = pageEvent;

    this.filterModel.valueChanges
      .pipe(
        startWith(''), // let one blank through to search for all
        debounceTime(100),
        distinctUntilChanged(),
        tap(searchTerm => this.sanitizer.sanitize(SecurityContext.URL, searchTerm))
      )
      .subscribe(val => {
        let query: null = null;
        if (val) {
          query = val.query;
        }

        this.organizationSearchEvent.query = query;

        this.loading.next(true);
        this.loadOrganizations();

        //due to ExpressionChangedAfterItHasBeenCheckedError when populating matBadge counter
        this.changeDetectorRef.detectChanges();
      });
  }

  private loadOrganizations() {
    this.organizationService.getOrganizations(this.organizationSearchEvent).subscribe({
      next: o => {
        this.organizationItems = o.items;
        this.selectedOrganizations.forEach(org => {
          const currentOrganization = this.organizationItems.find(id => id.id === org.id);

          if (currentOrganization) {
            currentOrganization.checked = true;
          }
        });
        this.selectedOrganizationCount = this.selectedOrganizations.length;
        this.selectAll = this.organizationItems.length === this.selectedOrganizations.length;
        this.loading.next(false);
      },
      error: () => {
        this.loading.next(false);
      },
    });
  }

  public organizationOptionWasClicked(organizationId: number) {
    const indexLocation = this.organizationItems.findIndex(v => v.id === organizationId);
    if (this.disableCheckboxes && !this.organizationItems[indexLocation].checked) {
      return;
    }
    const organization = this.organizationItems[indexLocation];
    organization.checked = !organization.checked;

    if (organization.checked) {
      this.selectedOrganizations = this.arrayUnique([...this.selectedOrganizations, organization]);
    } else {
      this.selectedOrganizations = this.selectedOrganizations.filter(selectedOrganization => selectedOrganization.id !== organizationId);
    }
    this.disableCheckboxes = this.maxSelectCount !== null && this.selectedOrganizations.length >= this.maxSelectCount;

    this.selectedOrganizationCount = this.selectedOrganizations.length;
    this.selectAll = this.organizationItems.length === this.selectedOrganizations.length;
  }

  private arrayUnique(orgs: Organization[]) {
    const a = orgs.concat();
    for (let i = 0; i < a.length; ++i) {
      for (let j = i + 1; j < a.length; ++j) {
        if (a[i].id === a[j].id) a.splice(j--, 1);
      }
    }

    return a;
  }
  public resetOrganizations() {
    this.selectedOrganizations = [];
    this.organizationItems.forEach(org => {
      org.checked = false;
    });
    this.organizationCheckboxes.setValue(this.selectedOrganizations);
    this.selectedOrganizationCount = this.selectedOrganizations.length;
    this.selectAll = false;
    this.disableCheckboxes = false;
  }

  public loadSelectedOrganizations() {
    this.loadedSelectedOrganizations = !this.loadedSelectedOrganizations;
    if (this.loadedSelectedOrganizations && this.selectedOrganizations.length > 0) {
      this.organizationItems = [...this.selectedOrganizations];
    } else {
      this.loadOrganizations();
    }
  }

  onSelectAllToggle() {
    this.loading.next(true);
    this.selectAll = !this.selectAll;
    if (!this.selectAll) {
      this.resetOrganizations();
    } else {
      this.organizationItems.forEach(org => {
        org.checked = this.selectAll;
      });
      this.selectedOrganizations = [...this.organizationItems];
      this.organizationCheckboxes.setValue(this.selectedOrganizations);
      this.selectedOrganizationCount = this.selectedOrganizations.length;
    }
    this.loading.next(false);
  }

  saveAndClose() {
    this.dialogRef.close(this.selectedOrganizations);
  }

  close() {
    this.dialogRef.close(false);
  }

  private initForm() {
    return new FormGroup({
      query: new FormControl(''),
    });
  }
}
